import * as yup from 'yup';
import type { CouponDraft } from '@/pages/Seminar/components/form/InstructorCouponDialog';
import type { InstructorCouponPayload } from '@/api/repository/types/types';
import {
  GENERAL_DEFAULT_CAPACITY,
  GENERAL_DEFAULT_LEVEL,
  ONLINE_GUIDE_MAX_LENGTH,
  SeminarFormatValue,
  SeminarScheduleValue,
  SeminarSessionRequest,
  SeminarTypeValue,
  SessionInput,
  TITLE_MAX_LENGTH,
  composeOpenSaleStart,
  composePeriodEnd,
  composePeriodStart,
  openEndPlaceholder,
  formatDateOnly,
  formatDateTimeLocal,
  isValidOnlineUrl,
  normalizeOnlineUrl,
  parseLocal,
  sortSessions,
  toSessionRequest,
  validateSessionList,
} from '@/pages/Seminar/components/form/seminarFormUtils';

/**
 * 세미나 등록·수정 폼의 값 모양·검증 스키마·요청 본문 변환·임시 저장.
 * 화면(단계 카드)과 떨어진 순수 규칙이라 폰 5단계·PC 3칸 배치가 같이 쓴다. (SeminarForm.tsx 에서 그대로 옮김)
 */

export interface Option {
  type: string;
  description: string;
}

export interface SeminarFormValues {
  /** 폼 검증 분기용 — 화면 상태(seminarType)와 같이 움직인다 */
  seminarType: SeminarTypeValue;
  title: string;
  description: string;
  instructor: string;
  categories: string[];
  level: string;
  /** 진행 방식 (커리어 전용) */
  format: SeminarFormatValue;
  /** 일정 유형 (커리어 전용, 동영상은 PERIOD 또는 OPEN(상시)) */
  schedule: SeminarScheduleValue;
  /** 당일(SINGLE) 시작·종료 일시 */
  startDateTime: string;
  endDateTime: string;
  /** 기간(PERIOD)·일반 세미나 시작일·종료일 (YYYY-MM-DD). 상시(OPEN)는 시작일 = 판매 시작일, 종료일은 쓰지 않는다 */
  periodStartDate: string;
  periodEndDate: string;
  /** 회차(SESSIONS) 목록 */
  sessions: SessionInput[];
  /** 수정 폼: 불러온 원래 시작·종료 — 날짜를 안 바꾸면 원래 시각을 지킨다 */
  originalStartDateTime: string;
  originalEndDateTime: string;
  /** 수정 폼: 불러온 원래 제목 — 40자 넘는 기존 제목이면 그대로 저장을 허용한다 */
  originalTitle: string;
  /** 문의 연락처 — 입력받지 않는다. 수정 시 서버 값을 그대로 돌려보낸다 */
  contact: string;
  recruitmentStartDateTime: string;
  recruitmentEndDateTime: string;
  location: string;
  address: string;
  addressCode?: string;
  latitude?: string;
  longitude?: string;
  stations?: string[];
  /** 라이브·동영상 접속(시청) 링크와 안내 */
  onlineUrl: string;
  onlineGuide: string;
  /** 일반 세미나 '온라인' 체크 — 켜면 주소를 받지 않는다 */
  isOnline: boolean;
  price: number;
  capacity: number;
  thumbnailUrl?: string;
}

/** 등록·수정 요청 본문 (서버 CreateSeminarRequest) */
export interface SeminarUpsertPayload {
  title: string;
  description: string;
  categories: string[];
  level: string;
  seminarType: SeminarTypeValue;
  startDateTime: string;
  endDateTime: string;
  recruitmentStartDateTime: string;
  /** 상시 동영상은 null(판매 종료 전까지 계속 받는다) */
  recruitmentEndDateTime: string | null;
  /** 상시 동영상은 비우면 null(인원 제한 없음) */
  maxParticipants: number | null;
  participationFee: number;
  address: string;
  addressDetail: string;
  addressCode?: string;
  latitude?: number;
  longitude?: number;
  nearbyStationIds: string[];
  thumbnailUrl: string | null;
  organizer: string;
  contact: string;
  isOnline: boolean;
  format?: SeminarFormatValue;
  schedule?: SeminarScheduleValue;
  sessions?: SeminarSessionRequest[];
  onlineUrl?: string | null;
  onlineGuide?: string | null;
  /** 신규 등록에서 함께 만들 강사 쿠폰(커리어 유료) — 서버가 세미나 저장 뒤 순서대로 만든다 */
  coupons?: InstructorCouponPayload[];
}

// ─── 일정·장소 분기 (검증과 전송이 같은 규칙을 쓴다) ─────────────────────────

export type ScheduleSource = Pick<
  SeminarFormValues,
  | 'seminarType'
  | 'format'
  | 'schedule'
  | 'startDateTime'
  | 'endDateTime'
  | 'periodStartDate'
  | 'periodEndDate'
  | 'sessions'
  | 'originalStartDateTime'
  | 'originalEndDateTime'
>;

export const isCareer = (values: Partial<SeminarFormValues>): boolean => values.seminarType === 'CAREER';

/** 실제로 쓰이는 일정 유형 — 일반은 늘 기간(시작일~종료일), 동영상은 기간 또는 상시, 상시는 동영상만 */
export const effectiveSchedule = (values: Partial<SeminarFormValues>): SeminarScheduleValue => {
  if (!isCareer(values)) return 'PERIOD';
  if (values.format === 'VOD') return values.schedule === 'OPEN' ? 'OPEN' : 'PERIOD';
  return values.schedule === 'OPEN' ? 'PERIOD' : (values.schedule ?? 'SINGLE');
};

/** 동영상 상시 판매 — 모집 기간·정원은 선택, 종료일 없음 */
export const isOpenSale = (values: Partial<SeminarFormValues>): boolean => effectiveSchedule(values) === 'OPEN';

/**
 * 쿠폰 검사·미리보기 기준 — 폼(앞 단계)에 지금 입력된 참가비·모집 마감 하나로 정한다.
 * 쿠폰 창의 기본 기간·"모집 마감 뒤로는…" 안내·달력 최대·검사·미리보기, 등록 전 초안 검사, 수정 화면의 "저장된 값과 다른지" 판정이 모두 이 값을 쓴다.
 * 상시 동영상은 모집 마감이 없다(null).
 */
export interface CouponBasis {
  listPrice: number;
  recruitmentEnd: string | null;
}

export const couponBasisOf = (values: Partial<SeminarFormValues>): CouponBasis => ({
  listPrice: Number(values.price) || 0,
  recruitmentEnd: isOpenSale(values) ? null : (values.recruitmentEndDateTime || '').slice(0, 16) || null,
});

export const sameCouponBasis = (a: CouponBasis, b: CouponBasis): boolean =>
  a.listPrice === b.listPrice && a.recruitmentEnd === b.recruitmentEnd;

/** 주소를 받아야 하는지 — 커리어 오프라인, 또는 일반인데 '온라인' 체크가 꺼진 경우 */
export const needsAddress = (values: Partial<SeminarFormValues>): boolean =>
  isCareer(values) ? (values.format ?? 'OFFLINE') === 'OFFLINE' : !values.isOnline;

/** 접속 링크를 받아야 하는지 — 커리어 라이브·동영상 */
export const needsOnlineUrl = (values: Partial<SeminarFormValues>): boolean =>
  isCareer(values) && (values.format === 'LIVE' || values.format === 'VOD');

/**
 * 서버에 보낼 startDateTime / endDateTime.
 * 기간형은 시작일 00:00 ~ 종료일 23:59(수정 폼에서 날짜를 그대로 두면 원래 시각), 회차형은 첫 회차 시작 ~ 마지막 회차 종료.
 */
export const resolveRange = (values: Partial<ScheduleSource>): { start: string; end: string } | null => {
  const schedule = effectiveSchedule(values);
  if (schedule === 'SESSIONS') {
    const sorted = sortSessions(values.sessions ?? []);
    if (sorted.length === 0) return null;
    return {
      start: toSessionRequest(sorted[0]).startDateTime,
      end: toSessionRequest(sorted[sorted.length - 1]).endDateTime,
    };
  }
  if (schedule === 'OPEN') {
    // 판매 시작 ~ 자리값(+10년). 종료는 화면 어디에도 쓰지 않는다
    if (!values.periodStartDate) return null;
    const start: string = composeOpenSaleStart(values.periodStartDate, values.originalStartDateTime);
    return { start, end: openEndPlaceholder(start) };
  }
  if (schedule === 'PERIOD') {
    if (!values.periodStartDate || !values.periodEndDate) return null;
    return {
      start: composePeriodStart(values.periodStartDate, values.originalStartDateTime),
      end: composePeriodEnd(values.periodEndDate, values.originalEndDateTime),
    };
  }
  if (!values.startDateTime || !values.endDateTime) return null;
  return { start: values.startDateTime.slice(0, 16), end: values.endDateTime.slice(0, 16) };
};

export const resolvedStart = (values: Partial<ScheduleSource>): Date | null => parseLocal(resolveRange(values)?.start);

/** 제목 서버 상한 (기존 긴 제목 수정 허용 범위) */
export const TITLE_HARD_MAX_LENGTH: number = 100;

/** 새 40자 규칙 이전에 등록된 긴 제목인지 — 이 경우 수정 시 40자 초과를 허용한다 */
export const isLegacyLongTitle = (originalTitle?: string): boolean => (originalTitle ?? '').trim().length > TITLE_MAX_LENGTH;

/** 빈 입력("")은 undefined 로 바꿔 required 의 친절한 문구가 뜨게 한다(NaN 캐스팅 원본 에러 방지) */
export const emptyToUndefined = (value: unknown, original: unknown) => (original === '' || original === null ? undefined : value);

// Yup 검증 스키마 — 커리어/일반, 진행 방식, 일정 유형에 따라 필요한 칸만 검사한다
export const schema = yup.object().shape({
  seminarType: yup.string(),

  title: yup.string()
    .trim()
    .required('세미나 제목을 입력해 주세요.')
    .min(3, '제목은 3자 이상 써 주세요.')
    .max(TITLE_HARD_MAX_LENGTH, `제목은 ${TITLE_HARD_MAX_LENGTH}자까지 쓸 수 있어요.`)
    // 40자는 새 규칙 — 이미 40자를 넘는 기존 제목은 수정 시 그대로 둘 수 있다(경고만)
    .test('title-40', `제목은 ${TITLE_MAX_LENGTH}자까지 쓸 수 있어요.`, function (value) {
      if (!value || value.trim().length <= TITLE_MAX_LENGTH) return true;
      return isLegacyLongTitle(this.parent.originalTitle);
    }),

  description: yup.string()
    .required('세미나 설명을 입력해 주세요.')
    .min(10, '설명은 10자 이상 써 주세요.'),

  instructor: yup.string()
    .required('강사명을 입력해 주세요.'),

  categories: yup.array()
    .test('career-categories', '분야를 1개 이상 선택해 주세요.', function (value) {
      return !isCareer(this.parent) || (value?.length ?? 0) > 0;
    }),

  level: yup.string()
    .test('career-level', '대상자 레벨을 선택해 주세요.', function (value) {
      return !isCareer(this.parent) || !!value;
    }),

  format: yup.string(),
  schedule: yup.string(),

  startDateTime: yup.string()
    .test('single-start', '시작일시를 선택해 주세요.', function (value) {
      return effectiveSchedule(this.parent) !== 'SINGLE' || !!value;
    })
    .test('single-future', '시작 시간은 지금 이후여야 해요.', function (value) {
      if (effectiveSchedule(this.parent) !== 'SINGLE' || !value) return true;
      return new Date(value) > new Date();
    }),

  endDateTime: yup.string()
    .test('single-end', '종료일시를 선택해 주세요.', function (value) {
      return effectiveSchedule(this.parent) !== 'SINGLE' || !!value;
    })
    .test('single-after-start', '종료일시는 시작일시보다 뒤여야 해요.', function (value) {
      const { startDateTime } = this.parent;
      if (effectiveSchedule(this.parent) !== 'SINGLE' || !value || !startDateTime) return true;
      return new Date(value) > new Date(startDateTime);
    }),

  periodStartDate: yup.string()
    .test('period-start', '시작일을 선택해 주세요.', function (value) {
      const schedule = effectiveSchedule(this.parent);
      if (schedule === 'OPEN' && !value) return this.createError({ message: '판매 시작일을 선택해 주세요.' });
      return schedule !== 'PERIOD' || !!value;
    })
    // 상시: 판매 시작일은 오늘 이후(오늘이면 바로 공개). 수정 폼에서 그대로 둔 날짜는 통과
    .test('open-start', '판매 시작일이 이미 지났어요.\n오늘 이후 날짜를 골라 주세요.', function (value) {
      if (effectiveSchedule(this.parent) !== 'OPEN' || !value) return true;
      if (value === (this.parent.originalStartDateTime ?? '').slice(0, 10)) return true;
      return value >= formatDateOnly(new Date());
    })
    // 커리어는 시작 전이어야 한다(환불 기준일). 일반은 안내용이라 진행 중인 세미나도 등록·수정할 수 있다.
    .test('period-future', '시작일이 이미 지났어요.\n내일 이후 날짜를 골라 주세요.', function (value) {
      if (!isCareer(this.parent) || effectiveSchedule(this.parent) !== 'PERIOD' || !value) return true;
      const start = resolvedStart(this.parent);
      return !start || start > new Date();
    }),

  periodEndDate: yup.string()
    .test('period-end', '종료일을 선택해 주세요.', function (value) {
      return effectiveSchedule(this.parent) !== 'PERIOD' || !!value;
    })
    .test('period-order', '종료일은 시작일과 같거나 뒤여야 해요.', function (value) {
      const { periodStartDate } = this.parent;
      if (effectiveSchedule(this.parent) !== 'PERIOD' || !value || !periodStartDate) return true;
      return value >= periodStartDate;
    })
    // 일반: 이미 끝난 세미나는 새로 알릴 필요가 없다 — 종료일은 오늘 이후(수정 폼에서 그대로 둔 종료일은 통과)
    .test('general-not-ended', '종료일이 이미 지났어요.\n오늘 이후 날짜를 골라 주세요.', function (value) {
      if (isCareer(this.parent) || !value) return true;
      if (value === (this.parent.originalEndDateTime ?? '').slice(0, 10)) return true;
      return value >= formatDateOnly(new Date());
    }),

  sessions: yup.array()
    .test('sessions', '회차를 확인해 주세요.', function (value) {
      if (effectiveSchedule(this.parent) !== 'SESSIONS') return true;
      const message = validateSessionList((value ?? []) as SessionInput[]);
      return message ? this.createError({ message }) : true;
    }),

  // 상시 동영상은 모집 기간을 받지 않는다(판매 시작부터 판매 종료 전까지)
  recruitmentStartDateTime: yup.string()
    .test('career-recruit-start', '모집 시작일시를 선택해 주세요.', function (value) {
      return !isCareer(this.parent) || isOpenSale(this.parent) || !!value;
    })
    .test('recruit-start-before-seminar', '모집 시작일시는 세미나 시작 전이어야 해요.', function (value) {
      if (!isCareer(this.parent) || isOpenSale(this.parent) || !value) return true;
      const start = resolvedStart(this.parent);
      return !start || new Date(value) <= start;
    }),

  recruitmentEndDateTime: yup.string()
    .test('career-recruit-end', '모집 종료일시를 선택해 주세요.', function (value) {
      return !isCareer(this.parent) || isOpenSale(this.parent) || !!value;
    })
    .test('recruit-end-after-start', '모집 종료일시는 모집 시작일시보다 뒤여야 해요.', function (value) {
      const { recruitmentStartDateTime } = this.parent;
      if (!isCareer(this.parent) || isOpenSale(this.parent) || !value || !recruitmentStartDateTime) return true;
      return new Date(value) > new Date(recruitmentStartDateTime);
    })
    .test('recruit-end-before-seminar', '모집 종료일시는 세미나 시작 전이어야 해요.', function (value) {
      if (!isCareer(this.parent) || isOpenSale(this.parent) || !value) return true;
      const start = resolvedStart(this.parent);
      return !start || new Date(value) <= start;
    }),

  address: yup.string()
    .test('address-required', '주소를 선택해 주세요.', function (value) {
      return !needsAddress(this.parent) || (value ?? '').trim().length >= 5;
    }),

  location: yup.string()
    .test('location-required', '상세 장소명을 2자 이상 입력해 주세요.', function (value) {
      return !needsAddress(this.parent) || (value ?? '').trim().length >= 2;
    }),

  onlineUrl: yup.string()
    .test('online-url-required', '링크를 입력해 주세요.', function (value) {
      return !needsOnlineUrl(this.parent) || !!value?.trim();
    })
    .test('online-url-format', '링크 주소를 확인해 주세요. (예: zoom.us/j/123…)', function (value) {
      if (!needsOnlineUrl(this.parent) || !value?.trim()) return true;
      return isValidOnlineUrl(value);
    }),

  onlineGuide: yup.string()
    .max(ONLINE_GUIDE_MAX_LENGTH, `안내는 ${ONLINE_GUIDE_MAX_LENGTH}자까지 쓸 수 있어요.`),

  isOnline: yup.boolean(),

  price: yup.number()
    .transform(emptyToUndefined)
    .typeError('참가비는 숫자로 입력해 주세요.')
    .required('참가비를 입력해 주세요.')
    .min(0, '참가비는 0원 이상이어야 해요.')
    .max(10000000, '참가비는 1,000만원 이하여야 해요.'),

  capacity: yup.number()
    .transform(emptyToUndefined)
    .typeError('정원은 숫자로 입력해 주세요.')
    .test('career-capacity', '정원을 1명 이상 입력해 주세요.', function (value) {
      // 상시 동영상은 정원 선택(비우면 제한 없음)
      if (isCareer(this.parent) && isOpenSale(this.parent)) return value == null || value >= 1;
      return !isCareer(this.parent) || (value != null && value >= 1);
    }),

  stations: yup.array().of(yup.string().required()),

  // 썸네일은 유형과 상관없이 필수(2026-09-29 사용자) — 목록 카드·상세 히어로에 쓰인다
  thumbnailUrl: yup.string()
    .nullable()
    .required('썸네일을 올려 주세요.'),
});

/** 폼 단계 번호 — 1 기본 정보 · 2 진행 방식·일정·장소 · 3 모집·참가비 · 4 소개 · 5 확인 */
export type SeminarFormStep = 1 | 2 | 3 | 4 | 5;

/** 칸 → 그 칸이 있는 단계 (검증에 걸리면 그 단계로 옮긴 뒤 칸에 포커스) */
export const FIELD_STEP: Partial<Record<keyof SeminarFormValues | 'coupons' | 'seminarType', SeminarFormStep>> = {
  seminarType: 1,
  title: 1,
  instructor: 1,
  categories: 1,
  level: 1,
  format: 2,
  schedule: 2,
  startDateTime: 2,
  endDateTime: 2,
  periodStartDate: 2,
  periodEndDate: 2,
  sessions: 2,
  isOnline: 2,
  address: 2,
  location: 2,
  stations: 2,
  onlineUrl: 2,
  onlineGuide: 2,
  price: 3,
  capacity: 3,
  recruitmentStartDateTime: 3,
  recruitmentEndDateTime: 3,
  coupons: 3,
  thumbnailUrl: 4,
  description: 4,
};

/** 화면에 보이는 순서 — 검증에 여러 칸이 걸리면 위에 있는 칸부터 알리고 포커스한다 */
export const FIELD_ORDER: Array<keyof SeminarFormValues> = [
  'title', 'instructor', 'categories', 'level',
  'startDateTime', 'endDateTime', 'periodStartDate', 'periodEndDate', 'sessions',
  'onlineUrl', 'onlineGuide', 'address', 'location',
  'price', 'capacity', 'recruitmentStartDateTime', 'recruitmentEndDateTime',
  'thumbnailUrl', 'description',
];

/**
 * 서버 400 문구로 틀린 칸을 짐작한다(서버 응답에 field 가 오면 그걸 쓴다).
 * 일정 칸은 지금 일정 유형에 보이는 칸으로 고른다.
 */
export const fieldFromServerMessage = (message: string, schedule: SeminarScheduleValue): keyof SeminarFormValues | null => {
  const startField: keyof SeminarFormValues = schedule === 'SINGLE' ? 'startDateTime' : schedule === 'SESSIONS' ? 'sessions' : 'periodStartDate';
  const endField: keyof SeminarFormValues = schedule === 'SINGLE' ? 'endDateTime' : schedule === 'SESSIONS' ? 'sessions' : 'periodEndDate';
  const rules: Array<[RegExp, keyof SeminarFormValues]> = [
    [/모집\s*시작/, 'recruitmentStartDateTime'],
    [/모집\s*(종료|마감)/, 'recruitmentEndDateTime'],
    [/제목/, 'title'],
    [/분야|카테고리/, 'categories'],
    [/레벨|대상/, 'level'],
    [/설명/, 'description'],
    [/썸네일|이미지/, 'thumbnailUrl'],
    [/회차/, 'sessions'],
    [/링크|URL|https/i, 'onlineUrl'],
    [/주소/, 'address'],
    [/장소/, 'location'],
    [/참가비|금액/, 'price'],
    [/정원|인원/, 'capacity'],
    [/시작/, startField],
    [/종료/, endField],
  ];
  return rules.find(([pattern]) => pattern.test(message))?.[1] ?? null;
};

export interface MetaCategory { code: string; displayName: string }
export interface MetaGroup { code: string; displayName: string; categories: MetaCategory[] }

export const targetLevels: Option[] = [
  { type: 'ANY', description: '무관' },
  { type: 'BEGINNER', description: '초급' },
  { type: 'INTERMEDIATE', description: '중급' },
  { type: 'ADVANCED', description: '고급' }
];

export const DRAFT_STORAGE_KEY = 'seminar_form_draft';

/** 임시 저장 보관 기간 — 이보다 오래된 초안은 버린다 */
const DRAFT_MAX_AGE_MS: number = 7 * 24 * 60 * 60 * 1000;

/** 임시 저장한 지하철역 — 칩에 이름을 보여 주려고 id 와 이름을 같이 둔다 */
export interface DraftStation {
  id: string;
  name: string;
  lineNo: string;
}

/**
 * 새 등록 임시 저장본(브라우저 저장소). 이미지는 파일이 아니라 이미 올린 주소(thumbnailUrl·설명 안 img)만 담긴다.
 */
export interface SeminarDraft {
  formValues: Partial<SeminarFormValues>;
  /** 함께 만들 쿠폰 초안 — 등록 실패 뒤 다시 열어도 남는다 */
  couponDrafts?: CouponDraft[];
  /** 화면에서 고른 유형(명치사 커리어/일반) */
  seminarType?: SeminarTypeValue;
  /** 고른 지하철역(칩 이름용) */
  stations?: DraftStation[];
  /** 마지막으로 보던 단계와 지나온 가장 먼 단계 */
  step?: number;
  maxStep?: number;
  /** 옛 드래프트 호환 — 예전엔 썸네일을 PC/모바일 배열로 보관했다 */
  pcImages?: string[];
  savedAt: number;
}

/** 저장소가 막힌 환경(사생활 보호 모드 등)에서도 폼은 그대로 쓰게 — 실패는 조용히 넘긴다. 저장했으면 저장 시각 */
export const saveDraft = (draft: Omit<SeminarDraft, 'savedAt'>): number | null => {
  try {
    const savedAt: number = Date.now();
    localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify({ ...draft, savedAt }));
    return savedAt;
  } catch {
    return null;
  }
};

export const loadDraft = (): SeminarDraft | null => {
  try {
    const raw = localStorage.getItem(DRAFT_STORAGE_KEY);
    if (!raw) return null;
    const draft: SeminarDraft = JSON.parse(raw);
    if (!draft || typeof draft !== 'object' || !draft.formValues) return null;
    // 7일 이상 된 드래프트는 무시
    if (Date.now() - (draft.savedAt ?? 0) > DRAFT_MAX_AGE_MS) {
      clearDraft();
      return null;
    }
    return draft;
  } catch {
    clearDraft();
    return null;
  }
};

export const clearDraft = (): void => {
  try {
    localStorage.removeItem(DRAFT_STORAGE_KEY);
  } catch {
    // 저장소를 못 쓰는 환경 — 지울 것도 없다
  }
};

/** 커리어 신규 등록 연락처 자리표시 — 서버가 커리어는 연락처를 필수로 받는다(폼에서는 입력받지 않음) */
export const CAREER_CONTACT_PLACEHOLDER: string = '000-0000-0000';

/** 서버 LocalDateTime 문자열 → 폼용 `YYYY-MM-DDTHH:mm` */
export const toFormDateTime = (value?: string | null): string => (value ? value.slice(0, 16) : '');

/** 검증을 통과한 폼 값 → 등록·수정 요청 본문. mobileThumbnailUrl 은 보내지 않는다(썸네일 1장 통일). */
export const buildPayload = (data: SeminarFormValues): SeminarUpsertPayload => {
  const range = resolveRange(data);
  if (!range) throw new Error('일정을 확인해 주세요.');
  const career: boolean = isCareer(data);
  const withAddress: boolean = needsAddress(data);

  const base = {
    title: data.title.trim(),
    description: data.description,
    seminarType: data.seminarType,
    startDateTime: range.start,
    endDateTime: range.end,
    participationFee: data.price,
    address: withAddress ? data.address : '',
    addressDetail: withAddress ? data.location.trim() : '',
    addressCode: withAddress && data.addressCode ? data.addressCode : undefined,
    latitude: withAddress && data.latitude ? parseFloat(data.latitude) : undefined,
    longitude: withAddress && data.longitude ? parseFloat(data.longitude) : undefined,
    nearbyStationIds: withAddress && data.stations && data.stations.length > 0 ? data.stations : [],
    thumbnailUrl: data.thumbnailUrl ? data.thumbnailUrl : null,
    organizer: data.instructor,
  };

  if (career) {
    const schedule = effectiveSchedule(data);
    const online: boolean = needsOnlineUrl(data);
    const open: boolean = schedule === 'OPEN';
    return {
      ...base,
      // 문의 연락처는 입력받지 않는다(문의처는 설명에 적는다). 수정은 서버 값 그대로, 신규 커리어는 서버 필수값이라 자리표시 번호
      contact: data.contact || CAREER_CONTACT_PLACEHOLDER,
      categories: data.categories,
      level: data.level,
      // 상시: 모집 = 판매 시작부터, 끝은 없음(판매 종료 버튼으로 멈춘다). 정원은 비우면 제한 없음
      recruitmentStartDateTime: open ? range.start : data.recruitmentStartDateTime,
      recruitmentEndDateTime: open ? null : data.recruitmentEndDateTime,
      maxParticipants: open ? (data.capacity && data.capacity >= 1 ? data.capacity : null) : data.capacity,
      isOnline: false,
      format: data.format,
      schedule,
      sessions: schedule === 'SESSIONS' ? sortSessions(data.sessions).map(toSessionRequest) : [],
      onlineUrl: online ? normalizeOnlineUrl(data.onlineUrl) : null,
      onlineGuide: online && data.onlineGuide.trim() ? data.onlineGuide.trim() : null,
    };
  }

  // 일반: 분야·레벨·정원·모집 기간은 입력받지 않는다 — 서버 필수값이라 기본값으로 채운다.
  // 모집 기간 = 지금(수정이면 원래 모집 시작) ~ 세미나 시작 (서버 규칙: 모집 종료 ≤ 세미나 시작)
  const originalRecruitStart = toFormDateTime(data.recruitmentStartDateTime);
  return {
    ...base,
    // 일반은 연락처를 받지 않는다 — 수정이면 서버 값 유지, 신규는 빈 값
    contact: data.contact || '',
    // 일반은 분야를 받지 않는다(서버 필수 아님)
    categories: [],
    level: data.level || GENERAL_DEFAULT_LEVEL,
    maxParticipants: data.capacity && data.capacity >= 1 ? data.capacity : GENERAL_DEFAULT_CAPACITY,
    recruitmentStartDateTime:
      originalRecruitStart && originalRecruitStart < range.start ? originalRecruitStart : formatDateTimeLocal(new Date()),
    recruitmentEndDateTime: range.start,
    isOnline: !!data.isOnline,
  };
};

// ─── 단계(폰 5단계 · PC 카드 5장) ─────────────────────────────────────────

export interface SeminarStepMeta {
  step: SeminarFormStep;
  /** PC 왼쪽 목록·확인 체크 목록에 쓰는 짧은 이름 */
  short: string;
  /** 폰 진행 막대 아래 "3/5 모집과 참가비" — 칸 이름이 따로 없으면 칸 이름으로도 쓴다 */
  progress: string;
  /** 폰 진행 막대 칸 이름(짧게 — 5칸이 폰 폭에 들어가게). 없으면 progress */
  bar?: string;
  /** 폰 단계 제목(큰 글씨 질문) */
  question: string;
  /** PC 카드 제목 */
  cardTitle: string;
}

export const STEP_META: SeminarStepMeta[] = [
  { step: 1, short: '기본 정보', progress: '어떤 세미나인가요', bar: '기본 정보', question: '어떤 세미나를 여나요?', cardTitle: '기본 정보' },
  { step: 2, short: '진행 방식·일정', progress: '진행 방식과 일정', bar: '일정', question: '언제, 어디서 하나요?', cardTitle: '진행 방식과 일정' },
  { step: 3, short: '모집·참가비', progress: '모집과 참가비', bar: '모집·참가비', question: '누가, 얼마에 신청하나요?', cardTitle: '모집과 참가비' },
  { step: 4, short: '소개(썸네일·설명)', progress: '소개', bar: '소개', question: '어떻게 소개할까요?', cardTitle: '소개(썸네일·설명)' },
  { step: 5, short: '확인', progress: '확인하고 등록', bar: '확인', question: '이렇게 보여요', cardTitle: '확인하고 등록' },
];

/** 입력 단계(1~4)에 속한 검증 칸 — [다음]은 이 칸들만 검사한다 */
export const fieldsOfStep = (step: SeminarFormStep): Array<keyof SeminarFormValues> =>
  FIELD_ORDER.filter((field) => FIELD_STEP[field] === step);

/**
 * 지금 값 전체를 같은 스키마로 검사해 칸별 첫 오류 문구를 모은다(화면 표시 전용 — 저장 검증은 handleSubmit 이 따로 한다).
 * 확인 단계 체크 목록·등록 버튼 활성·PC 빠진 항목 목록이 쓴다.
 */
export const collectErrors = (values: Partial<SeminarFormValues>): Partial<Record<keyof SeminarFormValues, string>> => {
  try {
    schema.validateSync(values, { abortEarly: false });
    return {};
  } catch (error) {
    const result: Partial<Record<keyof SeminarFormValues, string>> = {};
    if (error instanceof yup.ValidationError) {
      error.inner.forEach((item: yup.ValidationError) => {
        const key = (item.path ?? '').split(/[.[]/)[0] as keyof SeminarFormValues;
        if (key && !result[key]) result[key] = item.message;
      });
    }
    return result;
  }
};

/** 명치사 커리어 기본 플랫폼 수수료율(강사 약관 제1조, 부가세 포함) — 정산 예상 계산 상자용 */
export const PLATFORM_FEE_RATE: number = 0.15;
